/** Attribute set on `<html>` when the platform draws classic (layout-occupying) scrollbars. */
export const CLASSIC_SCROLLBARS_ATTRIBUTE = 'data-classic-scrollbars';

/**
 * Detects whether the platform uses classic scrollbars (they take layout space: Windows, Linux,
 * macOS with "Show scroll bars: Always") rather than overlay ones (macOS default, mobile), and
 * marks `<html>` with {@link CLASSIC_SCROLLBARS_ATTRIBUTE} if so.
 *
 * `sky-scrollbar.css` keys off the attribute: classic scrollbars styled through the standard
 * `scrollbar-width`/`scrollbar-color` properties get arrow buttons in Chromium, so on those
 * platforms `.sky-scrollbar` leaves the standard properties unset and the `::-webkit-scrollbar`
 * rules apply instead. Overlay scrollbars have no arrows and keep the thin standard styling.
 *
 * Call once at startup, before the first render.
 *
 * @returns `true` when classic scrollbars were detected.
 */
export const markClassicScrollbars = (doc: Document = document): boolean => {
  const probe = doc.createElement('div');
  probe.style.cssText =
    'position:absolute;top:-9999px;width:100px;height:100px;overflow:scroll;visibility:hidden';
  doc.body.appendChild(probe);
  const isClassic = probe.offsetWidth - probe.clientWidth > 0;
  probe.remove();

  doc.documentElement.toggleAttribute(CLASSIC_SCROLLBARS_ATTRIBUTE, isClassic);
  return isClassic;
};
